import { useMemo, useState, type FormEvent } from 'react';
import { Button, Field, Icon, Modal } from './ui';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { TreeNode } from '../lib/types';

/** What a child of each level is called, so the dialog can say it plainly. */
const CHILD_OF: Record<string, { label: string; blurb: string } | undefined> = {
  L1: { label: 'office', blurb: 'An office sits directly under the processor.' },
  L2: { label: 'agency', blurb: 'An agency writes business under this office.' },
  L3: {
    label: "agent's team",
    blurb: "An agent's own team, named after them. Their files belong to it.",
  },
};

function flatten(node: TreeNode, depth = 0): Array<{ node: TreeNode; depth: number }> {
  return [
    { node, depth },
    ...(node.children ?? []).flatMap((child) => flatten(child, depth + 1)),
  ];
}

/**
 * Adding a node to the tree.
 *
 * Level is not a field: it follows from the parent, because every scope query
 * in the system assumes depth and level agree. The dialog says which kind of
 * thing it is about to create instead of asking.
 */
export function AddOffice({
  open,
  onClose,
  onCreated,
  tree,
  defaultParentId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
  tree: TreeNode | null;
  defaultParentId?: string;
}) {
  const { user } = useAuth();
  const nodes = useMemo(() => (tree ? flatten(tree) : []), [tree]);

  const [parentId, setParentId] = useState(defaultParentId ?? user?.nodeId ?? '');
  const [name, setName] = useState('');
  const [principalName, setPrincipalName] = useState('');
  const [basisPoints, setBasisPoints] = useState('');
  const [perItem, setPerItem] = useState('');
  const [limit, setLimit] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parent = nodes.find((n) => n.node.id === parentId)?.node;
  const child = parent ? CHILD_OF[parent.level] : undefined;

  function reset() {
    setName('');
    setPrincipalName('');
    setBasisPoints('');
    setPerItem('');
    setLimit('');
    setError(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/org/nodes', {
        parentId,
        name: name.trim(),
        ...(principalName.trim() ? { principalName: principalName.trim() } : {}),
        ...(basisPoints ? { pricingFloorBasisPoints: Number(basisPoints) } : {}),
        ...(perItem ? { pricingFloorPerItemCents: Number(perItem) } : {}),
        ...(limit ? { equipmentApprovalLimitCents: Math.round(Number(limit) * 100) } : {}),
      });
      reset();
      onCreated?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={child ? `Add an ${child.label}` : 'Add to the hierarchy'}
      description={child?.blurb}
      onClose={() => {
        reset();
        onClose();
      }}
    >
      <form className="stack" onSubmit={submit}>
        {error && (
          <div className="notice notice--danger" role="alert">
            {Icon.alert({ size: 15 })}
            <span>{error}</span>
          </div>
        )}

        <Field
          label="Sits under"
          hint="Anywhere at or beneath your own office."
          htmlFor="office-parent"
        >
          <select
            id="office-parent"
            className="input select"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
          >
            {nodes.map(({ node, depth }) => (
              <option key={node.id} value={node.id} disabled={node.level === 'L4'}>
                {'  '.repeat(depth)}
                {node.name}
                <> · {node.level}</>
                {node.level === 'L4' ? ' (nothing sits below an agent)' : ''}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Name" htmlFor="office-name">
          <input
            id="office-name"
            className="input"
            required
            minLength={2}
            placeholder={child?.label === 'agency' ? 'Copperline Associates' : ''}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field
          label="Who runs it"
          hint="Optional. Shown on the hierarchy screen."
          htmlFor="office-principal"
        >
          <input
            id="office-principal"
            className="input"
            value={principalName}
            onChange={(e) => setPrincipalName(e.target.value)}
          />
        </Field>

        <Field
          label="Pricing floor"
          hint={
            parent
              ? `Leave blank to inherit ${parent.name}'s floor. It cannot be set below it — a child may not hold better terms than the office above it.`
              : undefined
          }
          htmlFor="office-bps"
        >
          <div className="row" style={{ gap: 8 }}>
            <input
              id="office-bps"
              className="input"
              inputMode="numeric"
              placeholder="bps"
              style={{ maxWidth: 110 }}
              value={basisPoints}
              onChange={(e) => setBasisPoints(e.target.value.replace(/\D/g, ''))}
            />
            <input
              className="input"
              inputMode="numeric"
              placeholder="per item, cents"
              style={{ maxWidth: 150 }}
              value={perItem}
              onChange={(e) => setPerItem(e.target.value.replace(/\D/g, ''))}
              aria-label="Per-item floor in cents"
            />
          </div>
        </Field>

        <Field
          label="Equipment approval limit"
          hint="Dollars this office may approve without asking its parent. Capped at the parent's own limit."
          htmlFor="office-limit"
        >
          <input
            id="office-limit"
            className="input"
            inputMode="decimal"
            placeholder="Inherit"
            style={{ maxWidth: 160 }}
            value={limit}
            onChange={(e) => setLimit(e.target.value.replace(/[^\d.]/g, ''))}
          />
        </Field>

        <div className="row">
          <Button type="submit" variant="primary" disabled={busy || name.trim().length < 2}>
            {busy ? 'Adding…' : child ? `Add ${child.label}` : 'Add'}
          </Button>
          <Button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Deleting a node.
 *
 * An empty office goes straight to the recycle bin. One with people, merchants
 * or files refuses first and comes back naming what is inside it — confirming
 * again takes the whole subtree together, and restoring the office later
 * brings all of it back in one go.
 */
export function DeleteOffice({
  node,
  onClose,
  onDeleted,
}: {
  node: { id: string; name: string } | null;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set when the office holds records, so the cascade has to be chosen. */
  const [holding, setHolding] = useState<string[] | null>(null);

  async function remove(cascade = false) {
    if (!node) return;
    setBusy(true);
    setError(null);
    try {
      await api.del(`/org/nodes/${node.id}${cascade ? '?cascade=true' : ''}`);
      setHolding(null);
      onDeleted?.();
      onClose();
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { holding?: string[] } | null) : null;
      if (body?.holding) setHolding(body.holding);
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={node !== null}
      title={`Delete ${node?.name ?? ''}?`}
      description="Deleting an office puts it in the recycle bin. Nothing is destroyed, and it can be put back."
      onClose={() => {
        setError(null);
        setHolding(null);
        onClose();
      }}
    >
      <div className="stack">
        {error && (
          <div className="notice notice--danger" role="alert">
            {Icon.alert({ size: 15 })}
            <span>{error}</span>
          </div>
        )}
        {holding && (
          <p className="muted" style={{ margin: 0 }}>
            Everything listed goes to the recycle bin together, and restoring the
            office brings all of it back in one go.
          </p>
        )}

        <div className="row">
          <Button
            variant="danger"
            disabled={busy}
            onClick={() => void remove(Boolean(holding))}
          >
            {busy
              ? 'Deleting…'
              : holding
                ? 'Delete it and everything in it'
                : 'Delete it'}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              setHolding(null);
              onClose();
            }}
          >
            Keep it
          </Button>
        </div>
      </div>
    </Modal>
  );
}
